'use client';

import { useEffect, useState } from 'react';
import { Bug } from 'lucide-react';

import { ReasonTip } from '@/components/ui/reason-tip';
import { useIssueReport } from './issue-report-context';

/** 이 표시가 붙은 곳을 누르면 모달이 「바깥을 눌렀다」고 닫지 않는다(`components/ui/dialog.tsx`). */
export const MODAL_ISSUE_REPORT_ATTR = 'data-modal-issue-report';

export function isModalIssueReportTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${MODAL_ISSUE_REPORT_ATTR}]`) !== null;
}

const SIZE = 24;
/** 창 왼쪽·아래 끝에서 아이콘까지. 오른쪽 위 닫기(×)와 마주 보는 자리다. */
const INSET = 12;
/** 아이콘이 창 내용과 겹치면 창 아래 안쪽 여백을 이만큼으로 늘려 자리를 낸다. */
const ROOM = INSET + SIZE + 8;

/** 그 자리에 무엇이 그려져 있으면 겹침이다 - 글자·입력·단추·그림, 또는 바탕·테두리가 있는 상자. */
const SOLID = 'button,a,input,textarea,select,img,svg,canvas,iframe,video,[role=button],[role=checkbox],[role=switch],[role=combobox],[contenteditable=true]';

function visibleColor(color: string): boolean {
  return color !== '' && color !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(color);
}

function paints(el: Element): boolean {
  if (el.matches(SOLID)) return true;
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
  }
  const style = getComputedStyle(el);
  if (visibleColor(style.backgroundColor) || (style.backgroundImage && style.backgroundImage !== 'none')) return true;
  if (style.boxShadow && style.boxShadow !== 'none') return true;
  return (['Top', 'Right', 'Bottom', 'Left'] as const).some(
    (side) => parseFloat(style[`border${side}Width`]) > 0 && visibleColor(style[`border${side}Color`]),
  );
}

function overlapsContent(content: HTMLElement, icon: { left: number; top: number }): boolean {
  const right = icon.left + SIZE;
  const bottom = icon.top + SIZE;
  for (const el of content.querySelectorAll('*')) {
    // 단추·그림 안쪽은 바깥 상자가 이미 그 자리를 덮는다.
    const solidAncestor = el.parentElement?.closest(SOLID);
    if (solidAncestor && content.contains(solidAncestor)) continue;
    const box = el.getBoundingClientRect();
    if (box.width <= 1 || box.height <= 1) continue;
    if (box.right <= icon.left || box.left >= right || box.bottom <= icon.top || box.top >= bottom) continue;
    if (getComputedStyle(el).visibility === 'hidden') continue;
    if (paints(el)) return true;
  }
  return false;
}

/** 창 아래 안쪽 여백을 늘려 아이콘 자리를 낸다. 이미 넉넉하면 그대로 두고 false. */
function makeRoom(content: HTMLElement): boolean {
  if ((parseFloat(getComputedStyle(content).paddingBottom) || 0) >= ROOM) return false;
  content.style.paddingBottom = `${ROOM}px`;
  return true;
}

function restoreRoom(content: HTMLElement, paddingBottom: string) {
  content.style.paddingBottom = paddingBottom;
}

/**
 * 모달이 떠 있으면 사이드바·머리말의 「문제 신고」가 막 뒤에 있다(10-01 데모 R9). 그래서 모든 모달
 * (`DialogContent`·`AlertDialogContent`) **창 안 왼쪽 아래 구석**에 신고 아이콘을 둔다(10-01 오후 다시 테스트 -
 * 창 바깥 막 위에 떠 있던 것을 사용자가 원래 말대로 창 안으로). 문제 신고 창 자신에도 같은 자리에 둔다 - 모든 창이
 * 같은 모양이어야 한다(사용자 16:20·17:26 두 번 신고). 신고 창에서 누르면 이미 열려 있으니 아무 일도 없다.
 *
 * 창 상자를 재서 `fixed` 로 둔다 - 안이 스크롤되는 창에서도 내용과 함께 흘러가지 않고, `overflow-hidden` 창에
 * 잘리지도 않는다. 그 구석에 창 내용(바닥 단추 줄 왼쪽 내용, 좁은 화면에서 폭을 꽉 채운 단추 등)이 있으면 창 아래
 * 안쪽 여백을 늘려 아이콘 자리를 낸다 - 창마다 고치지 않고 겹치게 두지도 않는다. 너무 작은 창에는 그리지 않는다.
 *
 * 창 크기·위치가 바뀌면(내용이 늘어남, 여는 애니메이션, 끌어 옮김, 화면 크기) 다시 잰다.
 */
export function ModalIssueReportButton({ content }: { content: HTMLElement | null }) {
  const issueReport = useIssueReport();
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!issueReport || !content) return;
    let checked = false;
    let roomMade = false;
    const savedPaddingBottom = content.style.paddingBottom;
    const place = () => {
      const box = content.getBoundingClientRect();
      if (box.width < SIZE * 4 || box.height < SIZE * 3) return setAt(null);
      const icon = {
        left: Math.max(0, box.left) + INSET,
        top: Math.min(box.bottom, window.innerHeight) - INSET - SIZE,
      };
      if (!checked && overlapsContent(content, icon)) {
        checked = true;
        // 창 크기가 바뀌면 ResizeObserver 가 다시 부른다(창이 가운데 정렬이면 아래 끝도 움직인다).
        roomMade = makeRoom(content);
      }
      setAt(icon);
    };
    // 창은 자리를 바꿀 때 0.2초 미끄러진다(`duration-200`, 끌어 옮기는 신고 창). 바뀐 순간만 재면 아이콘이 처음 자리에
    // 남으므로, 바뀐 뒤 잠깐은 그림마다 다시 잰다.
    let followUntil = 0;
    let frame = 0;
    const follow = () => {
      place();
      frame = performance.now() < followUntil ? requestAnimationFrame(follow) : 0;
    };
    const moved = () => {
      followUntil = performance.now() + 300;
      if (!frame) follow();
    };
    place();
    const resize = new ResizeObserver(moved);
    resize.observe(content);
    const restyle = new MutationObserver(moved);
    restyle.observe(content, { attributes: true, attributeFilter: ['style', 'class', 'data-state'] });
    content.addEventListener('animationend', place);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      restyle.disconnect();
      content.removeEventListener('animationend', place);
      window.removeEventListener('resize', place);
      if (roomMade) restoreRoom(content, savedPaddingBottom);
    };
  }, [issueReport, content]);

  if (!issueReport || !at) return null;
  return (
    <span
      {...{ [MODAL_ISSUE_REPORT_ATTR]: 'true' }}
      className="fixed z-50 flex"
      // 모달이 열려 있으면 Radix 가 body 에 pointer-events:none 을 건다 - 이 아이콘만 다시 받게 한다.
      style={{ left: at.left, top: at.top, pointerEvents: 'auto' }}
    >
      <ReasonTip reason="문제 신고">
        <button
          type="button"
          aria-label="문제 신고"
          onClick={issueReport.open}
          className="flex h-6 w-6 items-center justify-center rounded-full bg-background text-muted-foreground opacity-80 transition-colors hover:bg-muted hover:text-rose-600 hover:opacity-100"
        >
          <Bug className="h-4 w-4" aria-hidden="true" />
        </button>
      </ReasonTip>
    </span>
  );
}
